import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, X } from "lucide-react";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { PATHS } from "router/config/paths";
import { createdAssets, isJobNotStarted, MiroJobEntry, useMiroJobs } from "context/MiroJobsContext";
import { IMiroCanvasResult, IMiroExportResult, IMiroImportResult } from "api/miro/miro.type";
import { moodboardUserResults } from "utils/miroMoodboard";
import { summarizeCanvasResults } from "utils/miroCanvas";
import { boardMoodboardErrorKey, boardMoodboardOutcome, boardMoodboardStartedKey } from "utils/miroBoardMoodboard";
import { canvasImportText, exportCommentLines, exportNoteLines, joinLines } from "utils/miroCanvasMessages";

const openBoard = (link: string | null | undefined) => {
  if (!link) return; // never open about:blank
  window.open(link, "_blank", "noopener,noreferrer");
};

const boardLinkOf = (entry: MiroJobEntry) => entry.result?.board?.view_link ?? entry.board?.view_link ?? null;

const KIND_KEY = {
  export: "miro:tray.kind_export",
  import: "miro:tray.kind_import",
  moodboard: "miro:tray.kind_moodboard",
  canvas: "miro:tray.kind_canvas",
  canvas_import: "miro:tray.kind_canvas_import",
  board_moodboard: "miro:tray.kind_board_moodboard",
} as const;

/** One line saying how a settled job went. */
const summarize = (entry: MiroJobEntry, t: TFunction, exists: (key: string) => boolean): string => {
  if (isJobNotStarted(entry)) return t("miro:job.not_started");
  if (entry.phase === "failed") return t(entry.error === "timeout" ? "miro:job.timeout" : "miro:job.failed");

  const list = entry.result?.results ?? [];
  if (entry.kind === "import") {
    const results = list as IMiroImportResult[];
    const count = (status: string) => results.filter((r) => r.status === status).length;

    return t("miro:tray.summary_import", { imported: count("imported"), duplicate: count("duplicate"), failed: count("failed") });
  }

  if (entry.kind === "canvas_import") return canvasImportText(t, exists, entry.result) || t("miro:canvas_import.nothing_imported");

  if (entry.kind === "board_moodboard") {
    const { uploadId, split, code } = boardMoodboardOutcome(entry.result);

    return t(uploadId ? boardMoodboardStartedKey(split) : boardMoodboardErrorKey(code, exists) ?? "miro:board_moodboard.nothing_to_import");
  }

  if (entry.kind === "canvas") {
    const { created, skipped, failed, versions } = summarizeCanvasResults(list as IMiroCanvasResult[]);
    const line = t("miro:tray.summary_canvas", { created, skipped, failed });

    const withVersions = versions > 0 ? `${line} ${t("miro:canvas.versions_summary", { count: versions })}` : line;
    const commentLines = joinLines(exportCommentLines(t, exists, entry.result), exportNoteLines(t, exists, entry.result));

    return [withVersions, commentLines.done, ...commentLines.notes, ...commentLines.warnings].filter(Boolean).join(" ");
  }

  // A moodboard's title and section labels are layout, not the user's items.
  const results = (entry.kind === "moodboard" ? moodboardUserResults(list as IMiroExportResult[]) : list) as IMiroExportResult[];
  const created = results.filter((r) => r.status === "created").length;

  return t(entry.kind === "moodboard" ? "miro:moodboard.placed" : "miro:tray.summary_export", { created, total: results.length });
};

/**
 * The persistent home of Miro jobs the user sent to the background (REF-6702): a small pill at the bottom-right (above the snackbar column and the selection bar)
 * that opens a list of the running ones (N of M, link to the board) and the ones that finished (click for the
 * outcome; an import offers its edit step). Foreground jobs live in their own dialog and never appear here.
 * Mounted once, by the jobs provider.
 */
const MiroJobsTray = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { jobs, finished, dismissFinished, requestEditAssets, retryJob } = useMiroJobs();
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const running = Object.values(jobs).filter((job) => job.backgrounded && job.phase === "running");
  if (running.length === 0 && finished.length === 0) return null;

  return (
    // Bottom-right corner by default; lifted ONLY by the snackbar stack's real height while one is visible (`SnackbarProvider` publishes it), then eased back down.
    <div
      className="fixed bottom-[calc(1.25rem+var(--snackbar-bottom-clearance,0px)+var(--snackbar-stack-offset,0px))] right-5 z-[100001] flex flex-col items-end gap-2 transition-[bottom] duration-200 ease-out"
      data-testid="miro-jobs-tray"
    >
      {open && (
        <div className="w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-body p-3 shadow-lg" data-testid="miro-jobs-panel">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-h7 text-trueBlack dark:text-white">{t("miro:tray.heading")}</span>
            <button
              type="button"
              aria-label={t("miro:tray.close") as string}
              data-testid="miro-jobs-close"
              onClick={() => setOpen(false)}
              className="text-grey hover:text-trueBlack dark:hover:text-white"
            >
              <X size={16} />
            </button>
          </div>
          <ul className="flex max-h-[320px] flex-col gap-2 overflow-y-auto">
            {running.map((job) => {
              const progress = job.progress && job.progress.total > 0 ? job.progress : null;

              return (
                <li key={job.jobId} data-testid={`miro-job-row-${job.jobId}`} className="flex flex-col gap-1.5 rounded-md border border-black-300 p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-bodySm text-trueBlack dark:text-white">{t(KIND_KEY[job.kind])}</span>
                    <span className="shrink-0 text-bodySm text-grey" data-testid={`miro-job-progress-${job.jobId}`}>
                      {progress ? t("miro:job.progress", { done: Math.min(progress.done, progress.total), total: progress.total }) : t("miro:tray.working")}
                    </span>
                  </div>
                  <div className="h-1 w-full overflow-hidden rounded bg-black-300">
                    {progress ? (
                      <div
                        className="h-full rounded bg-purpleGreen transition-[width] duration-500"
                        style={{ width: `${Math.round((Math.min(progress.done, progress.total) / progress.total) * 100)}%` }}
                      />
                    ) : (
                      <div className="h-full w-1/3 animate-pulse rounded bg-purpleGreen" />
                    )}
                  </div>
                  {boardLinkOf(job) && (
                    <button
                      type="button"
                      data-testid={`miro-job-open-board-${job.jobId}`}
                      onClick={() => openBoard(boardLinkOf(job))}
                      className="self-start text-bodySm text-purpleGreen hover:underline"
                    >
                      {t("miro:send.open_board")}
                    </button>
                  )}
                </li>
              );
            })}
            {finished.map((job) => {
              const created = createdAssets(job);
              const expanded = detailId === job.jobId;

              return (
                <li key={job.jobId} data-testid={`miro-job-row-${job.jobId}`} className="flex flex-col gap-1.5 rounded-md border border-black-300 p-2.5">
                  <button
                    type="button"
                    data-testid={`miro-job-toggle-${job.jobId}`}
                    aria-expanded={expanded}
                    onClick={() => setDetailId(expanded ? null : job.jobId)}
                    className="flex items-center justify-between gap-2 text-start"
                  >
                    <span className="truncate text-bodySm text-trueBlack dark:text-white">{t(KIND_KEY[job.kind])}</span>
                    <span className={job.phase === "failed" ? "shrink-0 text-bodySm text-orange" : "shrink-0 text-bodySm text-purpleGreen"}>
                      {job.phase === "failed" ? t("miro:tray.failed") : t("miro:tray.finished")}
                    </span>
                  </button>
                  {expanded && (
                    <div className="flex flex-col gap-2" data-testid={`miro-job-details-${job.jobId}`}>
                      <span className="text-bodySm text-grey">{summarize(job, t, i18n.exists)}</span>
                      <div className="flex flex-wrap gap-2">
                        {job.phase === "succeeded" && boardLinkOf(job) && (
                          <button type="button" data-testid={`miro-job-open-board-${job.jobId}`} onClick={() => openBoard(boardLinkOf(job))} className="text-bodySm text-purpleGreen hover:underline">
                            {t("miro:send.open_board")}
                          </button>
                        )}
                        {job.kind === "canvas_import" && job.result?.canvas?.id && (
                          <button
                            type="button"
                            data-testid={`miro-job-open-canvas-${job.jobId}`}
                            onClick={() => {
                              navigate(`${PATHS.DESIGN_STUDIO}?id=${job.result?.canvas?.id}`);
                              setOpen(false);
                            }}
                            className="text-bodySm text-purpleGreen hover:underline"
                          >
                            {t("miro:canvas_import.open_canvas")}
                          </button>
                        )}
                        {created.length > 0 && (
                          <button
                            type="button"
                            data-testid={`miro-job-edit-${job.jobId}`}
                            onClick={() => {
                              requestEditAssets(created);
                              dismissFinished(job.jobId);
                              setOpen(false);
                            }}
                            className="text-bodySm text-purpleGreen hover:underline"
                          >
                            {t("miro:import.edit_assets")}
                          </button>
                        )}
                        {isJobNotStarted(job) && job.retryable && (
                          <button type="button" data-testid={`miro-job-retry-${job.jobId}`} onClick={() => void retryJob(job.jobId)} className="text-bodySm text-purpleGreen hover:underline">
                            {t("miro:job.retry")}
                          </button>
                        )}
                        <button type="button" data-testid={`miro-job-dismiss-${job.jobId}`} onClick={() => dismissFinished(job.jobId)} className="text-bodySm text-grey hover:underline">
                          {t("miro:tray.dismiss")}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <button
        type="button"
        data-testid="miro-jobs-pill"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex h-9 items-center gap-2 rounded-full border border-border bg-body px-3 text-h7 text-trueBlack shadow-md dark:text-white"
      >
        {running.length > 0 ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
        <span>{t("miro:tray.pill", { count: running.length > 0 ? running.length : finished.length, running: running.length > 0 ? 1 : 0 })}</span>
      </button>
    </div>
  );
};

export default MiroJobsTray;
